import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_PRODUCTS } from "../../graphql/AdminQueries";
import { formatPrice } from "../../utils/common";

const { Option } = Select;

const MIN_SEARCH_LEN = 3;
const DEFAULT_LIMIT = 20;
const DEBOUNCE_MS = 350;

const SelectProductAsync = ({
  tokenGet,
  required,
  form,
  setItemStatus,
  carOwnerId,
  carOwnerBonus,
  limit = DEFAULT_LIMIT,
  ...rest
}) => {
  const [searchText, setSearchText] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const requestSeq = useRef(0);

  const [fetchProducts, { loading }] = useLazyQuery(SEARCH_PRODUCTS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const term = (searchText || "").trim();

    if (term.length < MIN_SEARCH_LEN) {
      // Clear suggestions but keep selected value stable
      setProducts([]);
      return;
    }

    const currentSeq = ++requestSeq.current;
    const t = setTimeout(() => {
      fetchProducts({
        variables: {
          tokenGet,
          search: term,
          offset: 0,
          limit: limit,
        },
      }).then((res) => {
        if (currentSeq !== requestSeq.current) return;
        const data = res?.data?.getProducts?.items;
        setProducts(Array.isArray(data) ? data : []);
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [searchText, fetchProducts, tokenGet, limit]);

  const options = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      if (!p?.id) return;
      map.set(String(p.id), p);
    });
    if (selectedProduct?.id) {
      map.set(String(selectedProduct.id), selectedProduct);
    }
    return Array.from(map.values());
  }, [products, selectedProduct]);

  const notFoundContent = useMemo(() => {
    const term = (searchText || "").trim();
    if (term.length > 0 && term.length < MIN_SEARCH_LEN) {
      return <span>Zadaj aspoň 3 znaky</span>;
    }
    if (loading) {
      return <Spin size="small" />;
    }
    return <span>No results</span>;
  }, [searchText, loading]);

  const resolvePriceSale = (product) => {
    const priceLists = product?.priceLists;
    if (Array.isArray(priceLists) && carOwnerId != null) {
      const match = priceLists.find(
        (pl) => String(pl?.carOwner?.id) === String(carOwnerId)
      );
      if (match?.price_sale != null) return match.price_sale;
    }
    return product?.price_sale;
  };

  const renderOptionText = (product) => {
    const name = product?.name || "";
    const code = product?.code;
    const supplier = product?.supplier;
    if (supplier === "ayvens") {
      return `[${code || ""}] ${name} (Ayvens)`;
    }
    return name;
  };

  const applySelectionToForm = (product) => {
    if (!form || !product) return;
    const qty = form.getFieldValue("qty") ? form.getFieldValue("qty") : 1;
    const rawPriceSale = resolvePriceSale(product);
    const priceSale =
      rawPriceSale == null || Number.isNaN(Number(rawPriceSale))
        ? 0
        : Number(rawPriceSale);

    let updatedBonus = 0;
    let bonusPrice = 0;
    bonusPrice = priceSale;
    if (carOwnerBonus) {
      updatedBonus = (100 - carOwnerBonus) / 100;
      bonusPrice = updatedBonus ? priceSale / updatedBonus : priceSale;
    }

    if (typeof setItemStatus === "function") {
      setItemStatus(false);
    }

    form.setFieldsValue({
      priceSale: priceSale,
      bonusPrice: Number(bonusPrice).toFixed(2),
      total: formatPrice(bonusPrice * qty),
      productId: Number(product.id),
      productName: product.name,
      qty: qty,
      pricePurchase: product.price_purchase,
      tax: product.tax,
    });
  };

  return (
    <Select
      {...rest}
      showSearch
      allowClear
      placeholder="Položka: názov alebo kód, aspoň 3 znaky"
      optionFilterProp="children"
      required={required}
      filterOption={false}
      onSearch={(val) => setSearchText(val)}
      notFoundContent={notFoundContent}
      onSelect={(value) => {
        const product = options.find((p) => String(p?.id) === String(value));
        if (product) {
          setSelectedProduct(product);
          applySelectionToForm(product);
        }
      }}
      onClear={() => {
        setSearchText("");
        setProducts([]);
        setSelectedProduct(null);
        if (typeof setItemStatus === "function") {
          setItemStatus(true);
        }
        if (form) {
          form.setFieldsValue({
            productId: undefined,
            productName: undefined,
            priceSale: undefined,
            bonusPrice: undefined,
            total: undefined,
            pricePurchase: undefined,
            tax: undefined,
          });
        }
      }}
    >
      {options.map((p) => (
        <Option key={p.id} value={p.id} label={p.name}>
          {renderOptionText(p)}
        </Option>
      ))}
    </Select>
  );
};

export { SelectProductAsync };

